import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { 
  loadExerciseCategoryData,
  getAllExercises, 
  getExercisesGroupedByMainCategory, 
  searchExercises,
  getExerciseById,
  transformExerciseToStoreFormat 
} from '@/services/exerciseCategoryService'

export const useExercisePlanStore = create(
  persist(
    (set, get) => ({
      // State
      plans: [], // Start with empty plans - will be loaded from API
      selectedPlan: null,
      exerciseLibrary: [],
      exerciseCategories: [],
      exercisesGroupedByCategory: {},
      searchTerm: '',
      selectedFilters: {
        category: 'all',
        equipment: 'all',
        difficulty: 'all',
        muscleGroup: 'all'
      },
      isLoading: false,
      error: null,
      undoStack: [],
      redoStack: [],

      // Actions
      createPlan: (planData) => {
        const newPlan = {
          id: `plan-${Date.now()}`,
          ...planData,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isArchived: false,
          assignedClients: [],
          sections: [
            {
              id: `warmup-${Date.now()}`,
              name: 'Warm-up',
              type: 'warmup',
              exercises: []
            },
            {
              id: `main-${Date.now()}`,
              name: 'Main Workout',
              type: 'main',
              exercises: []
            },
            {
              id: `cooldown-${Date.now() + 1}`,
              name: 'Cooldown',
              type: 'cooldown',
              exercises: []
            }
          ]
        }
        
        set(state => ({
          plans: [...state.plans, newPlan],
          selectedPlan: newPlan
        }))
        
        return newPlan
      },

      updatePlan: (planId, updates) => {
        set(state => {
          const updatedPlans = state.plans.map(plan =>
            plan.id === planId 
              ? { ...plan, ...updates, updatedAt: new Date().toISOString() }
              : plan
          )
          
          return {
            plans: updatedPlans,
            selectedPlan: state.selectedPlan?.id === planId 
              ? { ...state.selectedPlan, ...updates, updatedAt: new Date().toISOString() }
              : state.selectedPlan
          }
        })
      },

      duplicatePlan: (planId) => {
        const { plans } = get()
        const planToDuplicate = plans.find(p => p.id === planId)
        
        if (planToDuplicate) {
          const duplicatedPlan = {
            ...planToDuplicate,
            id: `plan-${Date.now()}`,
            name: `${planToDuplicate.name} (Copy)`,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            assignedClients: [],
            sections: planToDuplicate.sections.map(section => ({
              ...section,
              id: `${section.type}-${Date.now()}-${Math.random()}`,
              exercises: section.exercises.map(exercise => ({
                ...exercise,
                id: `ex-${Date.now()}-${Math.random()}`
              }))
            }))
          }
          
          set(state => ({
            plans: [...state.plans, duplicatedPlan]
          }))
          
          return duplicatedPlan
        }
      },

      deletePlan: (planId) => {
        set(state => ({
          plans: state.plans.filter(plan => plan.id !== planId),
          selectedPlan: state.selectedPlan?.id === planId ? null : state.selectedPlan
        }))
      },

      archivePlan: (planId) => {
        set(state => ({
          plans: state.plans.map(plan =>
            plan.id === planId 
              ? { ...plan, isArchived: true, updatedAt: new Date().toISOString() }
              : plan
          )
        }))
      },

      setSelectedPlan: (plan) => {
        set({ selectedPlan: plan })
      },

      // Section management
      addSection: (planId, sectionData) => {
        const newSection = {
          id: `section-${Date.now()}`,
          ...sectionData,
          exercises: []
        }
        
        set(state => {
          const updatedPlans = state.plans.map(plan =>
            plan.id === planId
              ? {
                  ...plan,
                  sections: [...plan.sections, newSection],
                  updatedAt: new Date().toISOString()
                }
              : plan
          )
          
          return {
            plans: updatedPlans,
            selectedPlan: state.selectedPlan?.id === planId
              ? {
                  ...state.selectedPlan,
                  sections: [...state.selectedPlan.sections, newSection],
                  updatedAt: new Date().toISOString()
                }
              : state.selectedPlan
          }
        })
      },

      updateSection: (planId, sectionId, updates) => {
        set(state => {
          const updatedPlans = state.plans.map(plan =>
            plan.id === planId
              ? {
                  ...plan,
                  sections: plan.sections.map(section =>
                    section.id === sectionId ? { ...section, ...updates } : section
                  ),
                  updatedAt: new Date().toISOString()
                }
              : plan
          )
          
          return {
            plans: updatedPlans,
            selectedPlan: state.selectedPlan?.id === planId
              ? {
                  ...state.selectedPlan,
                  sections: state.selectedPlan.sections.map(section =>
                    section.id === sectionId ? { ...section, ...updates } : section
                  ),
                  updatedAt: new Date().toISOString()
                }
              : state.selectedPlan
          }
        })
      },

      deleteSection: (planId, sectionId) => {
        set(state => {
          const updatedPlans = state.plans.map(plan =>
            plan.id === planId
              ? {
                  ...plan,
                  sections: plan.sections.filter(section => section.id !== sectionId),
                  updatedAt: new Date().toISOString()
                }
              : plan
          )
          
          return {
            plans: updatedPlans,
            selectedPlan: state.selectedPlan?.id === planId
              ? {
                  ...state.selectedPlan,
                  sections: state.selectedPlan.sections.filter(section => section.id !== sectionId),
                  updatedAt: new Date().toISOString()
                }
              : state.selectedPlan
          }
        })
      },

      // Exercise management
      addExerciseToSection: (planId, sectionId, exerciseData) => {
        const newExercise = {
          id: `ex-${Date.now()}`,
          ...exerciseData,
          order: 0 // Will be updated by reorderExercises
        }
        
        set(state => {
          const updatedPlans = state.plans.map(plan =>
            plan.id === planId
              ? {
                  ...plan,
                  sections: plan.sections.map(section =>
                    section.id === sectionId
                      ? { ...section, exercises: [...section.exercises, newExercise] }
                      : section
                  ),
                  updatedAt: new Date().toISOString()
                }
              : plan
          )
          
          return {
            plans: updatedPlans,
            selectedPlan: state.selectedPlan?.id === planId
              ? {
                  ...state.selectedPlan,
                  sections: state.selectedPlan.sections.map(section =>
                    section.id === sectionId
                      ? { ...section, exercises: [...section.exercises, newExercise] }
                      : section
                  ),
                  updatedAt: new Date().toISOString()
                }
              : state.selectedPlan
          }
        })
      },

      updateExercise: (planId, sectionId, exerciseId, updates) => {
        set(state => {
          const updatedPlans = state.plans.map(plan =>
            plan.id === planId
              ? {
                  ...plan,
                  sections: plan.sections.map(section =>
                    section.id === sectionId
                      ? {
                          ...section,
                          exercises: section.exercises.map(exercise =>
                            exercise.id === exerciseId ? { ...exercise, ...updates } : exercise
                          )
                        }
                      : section
                  ),
                  updatedAt: new Date().toISOString()
                }
              : plan
          )
          
          return {
            plans: updatedPlans,
            selectedPlan: state.selectedPlan?.id === planId
              ? {
                  ...state.selectedPlan,
                  sections: state.selectedPlan.sections.map(section =>
                    section.id === sectionId
                      ? {
                          ...section,
                          exercises: section.exercises.map(exercise =>
                            exercise.id === exerciseId ? { ...exercise, ...updates } : exercise
                          )
                        }
                      : section
                  ),
                  updatedAt: new Date().toISOString()
                }
              : state.selectedPlan
          }
        })
      },

      deleteExercise: (planId, sectionId, exerciseId) => {
        set(state => {
          const updatedPlans = state.plans.map(plan =>
            plan.id === planId
              ? {
                  ...plan,
                  sections: plan.sections.map(section =>
                    section.id === sectionId
                      ? {
                          ...section,
                          exercises: section.exercises.filter(exercise => exercise.id !== exerciseId)
                        }
                      : section
                  ),
                  updatedAt: new Date().toISOString()
                }
              : plan
          )
          
          return {
            plans: updatedPlans,
            selectedPlan: state.selectedPlan?.id === planId
              ? {
                  ...state.selectedPlan,
                  sections: state.selectedPlan.sections.map(section =>
                    section.id === sectionId
                      ? {
                          ...section,
                          exercises: section.exercises.filter(exercise => exercise.id !== exerciseId)
                        }
                      : section
                  ),
                  updatedAt: new Date().toISOString()
                }
              : state.selectedPlan
          }
        })
      },

      reorderExercises: (planId, sectionId, exercises) => {
        const reorderedExercises = exercises.map((exercise, index) => ({
          ...exercise,
          order: index
        }))
        
        set(state => {
          const updatedPlans = state.plans.map(plan =>
            plan.id === planId
              ? {
                  ...plan,
                  sections: plan.sections.map(section =>
                    section.id === sectionId
                      ? { ...section, exercises: reorderedExercises }
                      : section
                  ),
                  updatedAt: new Date().toISOString()
                }
              : plan
          )
          
          return {
            plans: updatedPlans,
            selectedPlan: state.selectedPlan?.id === planId
              ? {
                  ...state.selectedPlan,
                  sections: state.selectedPlan.sections.map(section =>
                    section.id === sectionId
                      ? { ...section, exercises: reorderedExercises }
                      : section
                  ),
                  updatedAt: new Date().toISOString()
                }
              : state.selectedPlan
          }
        })
      },

      // Exercise library and search
      setSearchTerm: (term) => {
        set({ searchTerm: term })
      },

      setFilters: (filters) => {
        set(state => ({
          selectedFilters: { ...state.selectedFilters, ...filters }
        }))
      },

      getFilteredExercises: () => {
        const { exerciseLibrary, searchTerm, selectedFilters } = get()
        
        return exerciseLibrary.filter(exercise => {
          const matchesSearch = !searchTerm || 
            exercise.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            exercise.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            exercise.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
          
          const matchesCategory = selectedFilters.category === 'all' || 
            exercise.category === selectedFilters.category
          
          const matchesEquipment = selectedFilters.equipment === 'all' || 
            exercise.equipment === selectedFilters.equipment
          
          const matchesDifficulty = selectedFilters.difficulty === 'all' || 
            exercise.difficulty === selectedFilters.difficulty
          
          const matchesMuscleGroup = selectedFilters.muscleGroup === 'all' || 
            exercise.muscleGroups.includes(selectedFilters.muscleGroup)
          
          return matchesSearch && matchesCategory && matchesEquipment && 
                 matchesDifficulty && matchesMuscleGroup
        })
      },

      // Client assignment
      assignPlanToClient: (planId, clientId) => {
        set(state => ({
          plans: state.plans.map(plan =>
            plan.id === planId
              ? {
                  ...plan,
                  assignedClients: [...new Set([...plan.assignedClients, clientId])],
                  updatedAt: new Date().toISOString()
                }
              : plan
          )
        }))
      },

      unassignPlanFromClient: (planId, clientId) => {
        set(state => ({
          plans: state.plans.map(plan =>
            plan.id === planId
              ? {
                  ...plan,
                  assignedClients: plan.assignedClients.filter(id => id !== clientId),
                  updatedAt: new Date().toISOString()
                }
              : plan
          )
        }))
      },

      clearError: () => {
        set({ error: null })
      },

      // Exercise Library Management
      loadExerciseLibrary: async () => {
        try {
          set({ isLoading: true, error: null })
          
          // Load category data first to ensure it's cached
          await loadExerciseCategoryData()
          
          const allExercises = await getAllExercises()
          const exercisesGrouped = await getExercisesGroupedByMainCategory()
          
          // Transform exercises to store format
          const transformedExercises = allExercises.map(exercise => 
            transformExerciseToStoreFormat(exercise)
          )

          set({
            exerciseLibrary: transformedExercises,
            exercisesGroupedByCategory: exercisesGrouped,
            exerciseCategories: Object.keys(exercisesGrouped),
            isLoading: false,
            error: null
          })

          return transformedExercises
        } catch (error) {
          set({ 
            error: `Failed to load exercise library: ${error.message}`,
            isLoading: false 
          })
          throw error
        }
      },

      searchExerciseLibrary: async (searchTerm) => {
        try {
          const results = await searchExercises(searchTerm)
          return results.map(exercise => transformExerciseToStoreFormat(exercise))
        } catch (error) {
          console.error('Error searching exercises:', error)
          return []
        }
      },

      getExerciseById: async (exerciseId) => {
        try {
          const exercise = await getExerciseById(exerciseId)
          return exercise ? transformExerciseToStoreFormat(exercise) : null
        } catch (error) {
          console.error('Error getting exercise by ID:', error)
          return null
        }
      },

      getExercisesByCategory: (categoryName) => {
        const { exercisesGroupedByCategory } = get()
        return exercisesGroupedByCategory[categoryName] || []
      },

      // API Integration
      loadPlansFromAPI: async (apiData) => {
        set({ isLoading: true, error: null })
        
        try {
          const transformedPlans = apiData.map(program => {
            // Transform each program to match our store structure
            const transformedPlan = {
              id: program.id.toString(),
              name: program.name,
              description: program.name, // Using name as description since API doesn't provide separate description
              duration: 60, // Default duration, could be calculated from exercises
              difficulty: 'intermediate', // Default difficulty, could be determined from exercises
              assignedClients: [], // Would need to be populated from separate API call
              createdAt: program.created_at,
              updatedAt: program.updated_at,
              isArchived: !!program.deactivated_date,
              programStartDate: program.program_start_date,
              userId: program.user_id,
              goalId: program.goal_id,
              companyId: program.company_id,
              sections: []
            }

            // Transform exercise plans to sections
            if (program.exerciseplans && program.exerciseplans.length > 0) {
              program.exerciseplans.forEach(exercisePlan => {
                const section = {
                  id: exercisePlan.id.toString(),
                  name: exercisePlan.name,
                  type: 'main', // Default to main, could be determined from exercise plan properties
                  displayOrder: exercisePlan.display_order,
                  schedule: {
                    sunday: !!exercisePlan.sunday,
                    monday: !!exercisePlan.monday,
                    tuesday: !!exercisePlan.tuesday,
                    wednesday: !!exercisePlan.wednesday,
                    thursday: !!exercisePlan.thursday,
                    friday: !!exercisePlan.friday,
                    saturday: !!exercisePlan.saturday
                  },
                  exercises: []
                }

                // Transform exercises
                if (exercisePlan.exerciseplanexercises && exercisePlan.exerciseplanexercises.length > 0) {
                  section.exercises = exercisePlan.exerciseplanexercises.map(exercise => ({
                    id: exercise.id.toString(),
                    exerciseId: exercise.exercisetemplate_id.toString(),
                    name: exercise.name,
                    sets: exercise.sets,
                    reps: exercise.reps,
                    rest: parseInt(exercise.rest) || 0,
                    tempo: exercise.speed || '',
                    notes: exercise.note || '',
                    clientNotes: exercise.clientnote || '',
                    order: exercise.display_order,
                    category: {
                      id: exercise.cat_id,
                      name: exercise.cat_name
                    },
                    exerciseTemplate: exercise.exercisetemplate ? {
                      id: exercise.exercisetemplate.id,
                      name: exercise.exercisetemplate.name,
                      description: exercise.exercisetemplate.description,
                      videoUrl: exercise.exercisetemplate.video_url,
                      images: exercise.exercisetemplate.exercisetemplateimages || []
                    } : null,
                    // Additional fields from API
                    mode: exercise.mode,
                    intensity: exercise.intensity,
                    heartrate: exercise.heartrate,
                    duration: exercise.duration
                  }))
                }

                transformedPlan.sections.push(section)
              })
            }

            return transformedPlan
          })

          set({ 
            plans: transformedPlans,
            isLoading: false,
            error: null
          })

          return transformedPlans
        } catch (error) {
          set({ 
            isLoading: false, 
            error: `Failed to load plans: ${error.message}` 
          })
          throw error
        }
      },

      // Helper function to transform single plan from API
      transformPlanFromAPI: (program) => {
        const transformedPlan = {
          id: program.id.toString(),
          name: program.name,
          description: program.name,
          duration: 60,
          difficulty: 'intermediate',
          assignedClients: [],
          createdAt: program.created_at,
          updatedAt: program.updated_at,
          isArchived: !!program.deactivated_date,
          programStartDate: program.program_start_date,
          userId: program.user_id,
          goalId: program.goal_id,
          companyId: program.company_id,
          sections: []
        }

        if (program.exerciseplans && program.exerciseplans.length > 0) {
          program.exerciseplans.forEach(exercisePlan => {
            const section = {
              id: exercisePlan.id.toString(),
              name: exercisePlan.name,
              type: 'main',
              displayOrder: exercisePlan.display_order,
              schedule: {
                sunday: !!exercisePlan.sunday,
                monday: !!exercisePlan.monday,
                tuesday: !!exercisePlan.tuesday,
                wednesday: !!exercisePlan.wednesday,
                thursday: !!exercisePlan.thursday,
                friday: !!exercisePlan.friday,
                saturday: !!exercisePlan.saturday
              },
              exercises: []
            }

            if (exercisePlan.exerciseplanexercises && exercisePlan.exerciseplanexercises.length > 0) {
              section.exercises = exercisePlan.exerciseplanexercises.map(exercise => ({
                id: exercise.id.toString(),
                exerciseId: exercise.exercisetemplate_id.toString(),
                name: exercise.name,
                sets: exercise.sets,
                reps: exercise.reps,
                rest: parseInt(exercise.rest) || 0,
                tempo: exercise.speed || '',
                notes: exercise.note || '',
                clientNotes: exercise.clientnote || '',
                order: exercise.display_order,
                category: {
                  id: exercise.cat_id,
                  name: exercise.cat_name
                },
                exerciseTemplate: exercise.exercisetemplate ? {
                  id: exercise.exercisetemplate.id,
                  name: exercise.exercisetemplate.name,
                  description: exercise.exercisetemplate.description,
                  videoUrl: exercise.exercisetemplate.video_url,
                  images: exercise.exercisetemplate.exercisetemplateimages || []
                } : null,
                mode: exercise.mode,
                intensity: exercise.intensity,
                heartrate: exercise.heartrate,
                duration: exercise.duration
              }))
            }

            transformedPlan.sections.push(section)
          })
        }

        return transformedPlan
      }
    }),
    {
      name: 'exercise-plan-storage',
      partialize: (state) => ({
        plans: state.plans,
        exerciseLibrary: state.exerciseLibrary
      })
    }
  )
)

